export type AudioMode = 'silent' | 'minimal' | 'coach' | 'elite';
export type AudioIntensity = 'youth' | 'standard' | 'competition';

export type AudioCategory = 'master' | 'fx' | 'voice' | 'ui';

export type CueId =
    // UI (synthesized — no files needed)
    | 'ui_hover'
    | 'ui_press'
    | 'ui_tap'
    | 'ui_success'
    | 'ui_error'

    // Countdown voice (sample: /audio/count_N.mp3)
    | 'count_5'
    | 'count_4'
    | 'count_3'
    | 'count_2'
    | 'count_1'

    // Training FX (sample: /audio/*.mp3, synth fallback)
    | 'whistle'
    | 'wrestle' // New
    | 'begin'   // New
    | 'buzzer'
    | 'time'    // New
    | 'rest_start'
    | 'rest'    // New
    | 'transition'
    | 'complete'
    | 'break'   // New

    // Skill Tree (synthesized — no files needed)
    | 'unlock_path'
    | 'unlock_node'
    | 'pillar_neutral'
    | 'pillar_top'
    | 'pillar_bottom'
    | 'pillar_defense'
    | 'pillar_conditioning'

    // Stance & Motion (ElevenLabs Samples)
    | 'stance'
    | 'motion'
    | 'circle'
    | 'level_change'
    | 'sprawl'
    | 'shot';

/** Map of CueId → file path (relative to /public/) */
export const SAMPLE_MANIFEST: Partial<Record<CueId, string>> = {
    count_5: '/audio/count_5.mp3',
    count_4: '/audio/count_4.mp3',
    count_3: '/audio/count_3.mp3',
    count_2: '/audio/count_2.mp3',
    count_1: '/audio/count_1.mp3',
    whistle: '/audio/whistle.mp3',
    wrestle: '/audio/wrestle.mp3',
    begin: '/audio/begin.mp3',
    buzzer: '/audio/buzzer.mp3',
    time: '/audio/time.mp3',
    rest: '/audio/rest.mp3',
    break: '/audio/break.mp3',
    stance: '/audio/stance.mp3',
    motion: '/audio/motion.mp3',
    circle: '/audio/circle.mp3',
    level_change: '/audio/level_change.mp3',
    sprawl: '/audio/sprawl.mp3',
    shot: '/audio/shot.mp3',
};

/** Which category each cue routes through */
export const CUE_CATEGORY: Record<CueId, AudioCategory> = {
    ui_hover: 'ui',
    ui_press: 'ui',
    ui_tap: 'ui',
    ui_success: 'ui',
    ui_error: 'ui',

    count_5: 'voice',
    count_4: 'voice',
    count_3: 'voice',
    count_2: 'voice',
    count_1: 'voice',

    whistle: 'fx',
    wrestle: 'voice', // It's a voice shout
    begin: 'voice',   // It's a voice shout
    buzzer: 'fx',
    time: 'voice',    // It's a voice shout
    rest_start: 'fx',
    rest: 'voice',    // It's a voice shout
    transition: 'fx',
    complete: 'fx',
    break: 'voice',   // It's a voice shout

    unlock_path: 'fx',
    unlock_node: 'fx',
    pillar_neutral: 'fx',
    pillar_top: 'fx',
    pillar_bottom: 'fx',
    pillar_defense: 'fx',
    pillar_conditioning: 'fx',

    stance: 'voice',
    motion: 'voice',
    circle: 'voice',
    level_change: 'voice',
    sprawl: 'voice',
    shot: 'voice',
};

export interface AudioState {
    masterVolume: number; // 0-1
    fxVolume: number;
    voiceVolume: number;
    uiVolume: number;

    mode: AudioMode;
    intensity: AudioIntensity;
    hapticsEnabled: boolean;

    setVolume: (category: AudioCategory, val: number) => void;
    setMode: (mode: AudioMode) => void;
    setIntensity: (intensity: AudioIntensity) => void;
    toggleHaptics: () => void;
}
